/**
 * EmailField - Email input with lead capture on blur
 *
 * Features:
 * - Validates email format
 * - Captures lead on blur (before form submission)
 * - Shows checkmark when valid
 */

import { useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { FormField } from './FormField';

interface EmailFieldProps {
  /** react-hook-form registration object */
  registration: UseFormRegisterReturn;
  /** Error message from form validation */
  error?: string;
  /** Callback when email should be captured as lead */
  onLeadCapture?: (email: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
}

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function EmailField({
  registration,
  error,
  onLeadCapture,
  disabled,
}: EmailFieldProps) {
  const [value, setValue] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);

  const isValid = hasBlurred && EMAIL_REGEX.test(value) && !error;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    registration.onChange(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBlurred(true);
    registration.onBlur(e);

    // Capture lead if email is valid
    if (EMAIL_REGEX.test(e.target.value) && onLeadCapture) {
      onLeadCapture(e.target.value);
    }
  };

  return (
    <FormField
      label="Email"
      name={registration.name}
      ref={registration.ref}
      type="email"
      placeholder="you@example.com"
      autoComplete="email"
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      disabled={disabled}
    />
  );
}
